import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import { FlaskConical, Zap, AlertCircle } from "lucide-react";

const EXAMPLES = ["H2 + O2 = H2O", "C3H8 + O2 = CO2 + H2O", "Fe + O2 = Fe2O3", "KMnO4 + HCl = KCl + MnCl2 + H2O + Cl2"];

function BalancerResult({ result }) {
  return (
    <div className="mt-6 space-y-4" data-testid="balancer-result">
      <div className="rounded-md border border-lime-400/40 bg-lime-500/5 p-5">
        <div className="text-[10px] uppercase tracking-widest text-lime-300">Balanced</div>
        <div className="font-mono-num mt-2 text-2xl font-semibold text-white break-all">{result.balanced}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {[["Reactants", result.reactants], ["Products", result.products]].map(([label, list]) => (
          <div key={label} className="rounded-md border border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="font-mono-num mt-2 text-slate-200">{list.join(", ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Examples({ onPick }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">Try one of these</div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="balancer-examples">
        {EXAMPLES.map((ex) => (
          <button key={ex} onClick={() => onPick(ex)} data-testid={`balancer-example-${ex.replace(/\s+/g, "-")}`} className="font-mono-num rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300 hover:border-lime-400/40 hover:text-lime-200">
            {ex}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Balancer() {
  const [eq, setEq] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (input) => {
    const equation = (input ?? eq).trim();
    if (!equation) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    if (input !== undefined) setEq(input);
    try {
      const { data } = await api.post("/balancer", { equation });
      setResult(data);
    } catch (e) {
      setErr(apiError(e, "Could not balance this equation."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="balancer-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-lime-400/30 bg-lime-500/10 px-3 py-1 text-xs uppercase tracking-widest text-lime-300"><FlaskConical size={12} /> Equation Balancer</div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Balance any reaction, instantly.</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Type an unbalanced equation using <code className="font-mono-num rounded bg-white/10 px-1">=</code> or <code className="font-mono-num rounded bg-white/10 px-1">-{'>'}</code> and let the algorithm do the algebra.
        </p>
      </div>

      <div className="rounded-md border border-white/10 bg-black/40 p-6">
        <label className="text-[10px] uppercase tracking-widest text-slate-500">Enter equation</label>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
          <input value={eq} onChange={(e) => setEq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. C3H8 + O2 = CO2 + H2O" data-testid="balancer-input"
            className="font-mono-num min-w-0 flex-1 rounded-md border border-white/10 bg-black/60 px-4 py-3 text-base text-slate-100 placeholder:text-slate-500 focus:border-lime-400/60 focus:outline-none focus:ring-2 focus:ring-lime-400/30" />
          <button onClick={() => run()} disabled={busy || !eq.trim()} data-testid="balancer-run-btn" className="inline-flex items-center justify-center gap-2 rounded-md bg-lime-500 px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-lime-400 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-slate-500">
            <Zap size={14} /> Balance
          </button>
        </div>
        {err && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="balancer-error"><AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{err}</span></div>
        )}
        {result && <BalancerResult result={result} />}
      </div>

      <Examples onPick={run} />
    </div>
  );
}
